import {SdkConfig, Vault} from "../types";
import {getFullnodeUrl, SuiClient, SuiObjectResponse} from "@mysten/sui/client";
import {MathUtil} from "../utils/math";
import {MmtSDK} from "@mmt-finance/clmm-sdk";
import {MMT_ROUTER_PACKAGE_ID, PYTH_ORACLE_MAP, VAULT_PACKAGE_ID} from "../constant";
import {Transaction} from "@mysten/sui/transactions";
import {getObjectFields, getObjectType} from "../utils/objects";
import {extractStructTagFromType} from "../utils/utils";
import {handleMessageError, VaultsErrorCode} from "../errors";
import {
  CLOCK_ADDRESS,
  COIN_DECIMALS_REGISTRY,
  MMT_CLMM_VERSION_ID,
  MMT_ORACLE_ID,
  MMT_ORACLE_PACKAGE_ID,
  ORACLE_ADMIN_CAP_ID, PRICE_ADAPTER_ADMIN_CAP, PRICE_ADAPTER_PACKAGE_ID,
  REGISTRY_ADMIN_CAP_ID, REGISTRY_ID, VAULT_ADMIN_CAP,
  VAULT_VERSION_ID
} from "../constant";
import {TickMath} from "../utils/tickMath";
import BN from "bn.js";
import Decimal from "../utils/decimal";

export class Admin {
  static readonly FEE_FACTOR = 1000000;
  protected _config: SdkConfig
  protected _senderAddress: string

  constructor(config: SdkConfig, senderAddress: string) {
    this._config = config
    this._senderAddress = senderAddress
  }

  setSenderAddress(value: string): void {
    this._senderAddress = value;
  }

  getSenderAddress(): string {
    return this._senderAddress
  }

  getConfig(): SdkConfig {
    return this._config
  }

  static createSDK(options: { fullNodeUrl?: string, senderAddress: string }): Admin {
    let suiClient;
    let mmtClmmSDK;
    if (!options.fullNodeUrl) {
      suiClient = new SuiClient({url: getFullnodeUrl('mainnet')});
      mmtClmmSDK = MmtSDK.NEW({network: "mainnet"})
    } else {
      suiClient = new SuiClient({url: options.fullNodeUrl});
      mmtClmmSDK = MmtSDK.NEW({network: "mainnet", suiClientUrl: options.fullNodeUrl})
    }
    return new Admin({suiClient: suiClient, mmtClmmSDK: mmtClmmSDK}, options.senderAddress);
  }


  public async newUncorrelatedVault(param: UncorrelatedVaultParam, tx?: Transaction): Promise<Transaction> {
    const upper_price_scalling = MathUtil.toX64Decimal(
      new Decimal(param.upper_price_scalling)).toFixed(0, Decimal.ROUND_UP);
    const lower_price_scalling = MathUtil.toX64Decimal(
      new Decimal(param.lower_price_scalling)).toFixed(0, Decimal.ROUND_UP);
    const slippage_up = MathUtil.toX64Decimal(
      Decimal(param.slippage_up).add(1)).toFixed(0, Decimal.ROUND_UP);
    const slippage_down = MathUtil.toX64Decimal(
      Decimal(1).sub(Decimal(param.slippage_down))).toFixed(0, Decimal.ROUND_UP);

    const pool = await this._config.mmtClmmSDK.Pool.getPool(param.pool_id);
    const curr_price = MathUtil.fromX64Decimal(new Decimal(pool.currentSqrtPrice));
    const upper_trigger_price_scalling = curr_price.mul(new Decimal(param.upper_price_scalling));
    const lower_trigger_price_scalling = curr_price.mul(new Decimal(param.lower_price_scalling));
    const fee_val = Decimal(param.fee_val).mul(Decimal(1000000)).toFixed(0, Decimal.ROUND_UP);
    const withdraw_fee_val = Decimal(param.withdraw_fee_val).mul(Decimal(1000000)).toFixed(0, Decimal.ROUND_UP);

    tx = tx || new Transaction();
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::vault::new_uncorrelated_vault`,
      typeArguments: [param.coin_type_a, param.coin_type_b, param.coin_type_token],
      arguments: [
        tx.object(param.pool_id),
        tx.object(param.treasury_cap_id),
        tx.pure.u128(upper_price_scalling),
        tx.pure.u128(lower_price_scalling),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(upper_trigger_price_scalling)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(lower_trigger_price_scalling)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(slippage_up),
        tx.pure.u128(slippage_down),
        tx.pure.u64(param.free_threshold_a),
        tx.pure.u64(param.free_threshold_b),
        tx.pure.u64(param.lock_threshold_a),
        tx.pure.u64(param.lock_threshold_b),
        tx.pure.u64(fee_val),
        tx.pure.u64(withdraw_fee_val),
        tx.pure.u8(param.decimals_a),
        tx.pure.u8(param.decimals_b),
        tx.pure.u64(param.deposit_limit),
        tx.pure.string(param.target_adapter),
        tx.pure.bool(param.is_target_reverse),
      ],
    });

    return tx;
  }

  public async newStableVault(param: StableVaultParam, tx?: Transaction): Promise<Transaction> {
    const upper_price_scalling = MathUtil.toX64Decimal(
      new Decimal(param.upper_price_scalling)).toFixed(0, Decimal.ROUND_UP);
    const lower_price_scalling = MathUtil.toX64Decimal(
      new Decimal(param.lower_price_scalling)).toFixed(0, Decimal.ROUND_UP);
    const slippage_up = MathUtil.toX64Decimal(
      Decimal(param.slippage_up).add(1)).toFixed(0, Decimal.ROUND_UP);
    const slippage_down = MathUtil.toX64Decimal(
      Decimal(1).sub(Decimal(param.slippage_down))).toFixed(0, Decimal.ROUND_UP);
    const fee_val = Decimal(param.fee_val).mul(Decimal(1000000)).toFixed(0, Decimal.ROUND_UP);
    const withdraw_fee_val = Decimal(param.withdraw_fee_val).mul(Decimal(1000000)).toFixed(0, Decimal.ROUND_UP);

    tx = tx || new Transaction();
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::vault::new_stable_vault`,
      typeArguments: [param.coin_type_a, param.coin_type_b, param.coin_type_token],
      arguments: [
        tx.object(param.pool_id),
        tx.object(param.treasury_cap_id),
        tx.pure.u128(upper_price_scalling),
        tx.pure.u128(lower_price_scalling),
        tx.pure.u128(slippage_up),
        tx.pure.u128(slippage_down),
        tx.pure.u64(param.free_threshold_a),
        tx.pure.u64(param.free_threshold_b),
        tx.pure.u64(fee_val),
        tx.pure.u64(withdraw_fee_val),
        tx.pure.u8(param.decimals_a),
        tx.pure.u8(param.decimals_b),
        tx.pure.u64(param.deposit_limit),
      ],
    });

    return tx;
  }

  public async newDriftVault(param: DriftVaultParam, tx?: Transaction): Promise<Transaction> {
    const upper_price_scalling = MathUtil.toX64Decimal(
      new Decimal(param.upper_price_scalling)).toFixed(0, Decimal.ROUND_UP);
    const lower_price_scalling = MathUtil.toX64Decimal(
      new Decimal(param.lower_price_scalling)).toFixed(0, Decimal.ROUND_UP);
    const slippage_up = MathUtil.toX64Decimal(
      Decimal(param.slippage_up).add(1)).toFixed(0, Decimal.ROUND_UP);
    const slippage_down = MathUtil.toX64Decimal(
      Decimal(1).sub(Decimal(param.slippage_down))).toFixed(0, Decimal.ROUND_UP);

    const pool = await this._config.mmtClmmSDK.Pool.getPool(param.pool_id);
    const curr_price = MathUtil.fromX64Decimal(new Decimal(pool.currentSqrtPrice));
    const upper_trigger_price_scalling = curr_price.mul(new Decimal(param.upper_price_scalling));
    const fee_val = Decimal(param.fee_val).mul(Decimal(1000000)).toFixed(0, Decimal.ROUND_UP);
    const withdraw_fee_val = Decimal(param.withdraw_fee_val).mul(Decimal(1000000)).toFixed(0, Decimal.ROUND_UP);

    tx = tx || new Transaction();
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::vault::new_drift_vault`,
      typeArguments: [param.coin_type_a, param.coin_type_b, param.coin_type_token],
      arguments: [
        tx.object(param.pool_id),
        tx.object(param.treasury_cap_id),
        tx.pure.u128(upper_price_scalling),
        tx.pure.u128(lower_price_scalling),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(upper_trigger_price_scalling)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(slippage_up),
        tx.pure.u128(slippage_down),
        tx.pure.u64(param.free_threshold_a),
        tx.pure.u64(param.free_threshold_b),
        tx.pure.u64(param.lock_threshold_a),
        tx.pure.u64(param.lock_threshold_b),
        tx.pure.u64(fee_val),
        tx.pure.u64(withdraw_fee_val),
        tx.pure.u8(param.decimals_a),
        tx.pure.u8(param.decimals_b),
        tx.pure.u64(param.deposit_limit),
        tx.pure.string(param.target_adapter),
      ],
    });

    return tx;
  }

  public async seed(param: SeedParam, tx?: Transaction) {
    const item: any = await this._config.suiClient.getObject({
      id: param.vault_id,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    })
    const vault = await this.buildVaultWithoutLiquidityAndPosition(item);
    tx = tx || new Transaction();

    const coinA = tx.splitCoins(tx.object(param.asset_a), [Math.pow(10, 5)]);
    const coinB = tx.splitCoins(tx.object(param.asset_b), [Math.pow(10, 5)]);
    const seedReceipt = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::init_vault::seed`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(param.vault_id),
        tx.object(param.vault_cap),
        tx.object(vault.clmm_pool_id),
        tx.pure.u64(Math.pow(10, 5)),
        coinA,
        coinB,
        tx.object(VAULT_VERSION_ID),
        tx.object(MMT_CLMM_VERSION_ID),
        tx.object(CLOCK_ADDRESS),
      ],
    });

    if (vault.config_type.includes('Stable')) {
      tx.moveCall({
        target: `${VAULT_PACKAGE_ID}::init_vault::stable_seed_handler`,
        typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
        arguments: [
          tx.object(param.vault_id),
          seedReceipt,
          tx.object(VAULT_VERSION_ID),
        ],
      });
    } else if (vault.config_type.includes('Drift')) {
      tx.moveCall({
        target: `${VAULT_PACKAGE_ID}::init_vault::drift_vault_seed_handler`,
        typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
        arguments: [
          tx.object(param.vault_id),
          seedReceipt,
          tx.object(VAULT_VERSION_ID),
        ],
      });
    } else if (vault.config_type.includes('Uncorrelated')) {
      tx.moveCall({
        target: `${VAULT_PACKAGE_ID}::init_vault::uncorrelated_seed_handler`,
        typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
        arguments: [
          tx.object(param.vault_id),
          seedReceipt,
          tx.object(VAULT_VERSION_ID),
        ],
      });
    } else {
      return handleMessageError(VaultsErrorCode.InvalidParam, 'vault config type is not supported');
    }

    return tx;
  }

  private async getVault(vaultId: string): Promise<Omit<Vault, 'liquidity' | 'position' | 'pool'>> {
    const objects = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });

    if (objects.error) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `Vault with id ${vaultId} not found`);
    }

    const vault = await this.buildVaultWithoutLiquidityAndPosition(objects);
    if (!vault) {
      return handleMessageError(VaultsErrorCode.BuildError, 'buildVault error');
    }
    return vault;
  }

  private async buildVaultWithoutLiquidityAndPosition(objects: SuiObjectResponse): Promise<Omit<Vault, 'liquidity' | 'position' | 'pool'>> {
    const fields = getObjectFields(objects)
    const type = getObjectType(objects) as string
    console.log("getObjectType", fields, type);
    if (fields) {
      const clmm_pool_id = fields.clmm_pool_id;
      const pool = await this._config.mmtClmmSDK.Pool.getPool(clmm_pool_id);
      console.log("getPool", pool);
      const sqrt_price = pool.currentSqrtPrice;

      let is_lock = false;
      if (!Decimal(fields.lock_threshold_a).equals(0) || !Decimal(fields.lock_threshold_b).equals(0)) {
        is_lock = Decimal(fields.free_balance_a).greaterThanOrEqualTo(Decimal(fields.lock_threshold_a)) ||
          Decimal(fields.free_balance_b).greaterThanOrEqualTo(fields.lock_threshold_b)
      }

      let is_deposit_enabled = false;
      const dynamicFieldPage = await this._config.suiClient.getDynamicFields({parentId: fields.id.id});
      for (const data of dynamicFieldPage.data) {
        const value = data.name.value as Iterable<number>;
        const name = new TextDecoder().decode(new Uint8Array(value));
        if (name === 'deposit_enabled') {
          let objectId = data.objectId as string;
          let deposit_enabled_object = await this._config.suiClient.getObject({
            id: objectId,
            options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
          });
          let objectFields = getObjectFields(deposit_enabled_object);
          if (objectFields) {
            is_deposit_enabled = objectFields.value;
          }
        }
      }

      let suiStructTag = extractStructTagFromType(type);
      console.log("suiStructTag", suiStructTag);

      const price = TickMath.sqrtPriceX64ToPrice(new BN(pool.currentSqrtPrice), fields.decimals_a, fields.decimals_b);
      const upper_trigger_price_scalling = price.mul(MathUtil.u64ToRatio(fields.upper_price_scalling));
      const lower_trigger_price_scalling = price.mul(MathUtil.u64ToRatio(fields.lower_price_scalling));

      return {
        id: fields.id.id,
        clmm_pool_id: fields.clmm_pool_id,
        free_balance_a: fields.free_balance_a,
        free_balance_b: fields.free_balance_b,
        fee_a: fields.fee_a,
        fee_b: fields.fee_b,
        seed_balance: fields.seed_balance,
        upper_price_scalling: fields.upper_price_scalling,
        lower_price_scalling: fields.lower_price_scalling,
        upper_price_scalling_dec: MathUtil.u64ToRatio(fields.upper_price_scalling),
        lower_price_scalling_dec: MathUtil.u64ToRatio(fields.lower_price_scalling),
        lower_trigger_price: fields.config.fields.t.fields.lower_trigger_price,
        upper_trigger_price: fields.config.fields.t.fields.upper_trigger_price,
        upper_trigger_price_scalling_dec: upper_trigger_price_scalling,
        lower_trigger_price_scalling_dec: lower_trigger_price_scalling,
        last_rebalance_sqrt_price: fields.last_rebalance_sqrt_price,
        last_rebalance_sqrt_price_dec: MathUtil.u64ToRatio(fields.last_rebalance_sqrt_price),
        last_rebalance_time: fields.last_rebalance_time,
        deposit_limit: fields.deposit_limit,
        free_threshold_a: fields.free_threshold_a,
        free_threshold_b: fields.free_threshold_b,
        lock_threshold_a: fields.lock_threshold_a,
        lock_threshold_b: fields.lock_threshold_b,

        slippage_up: fields.slippage_up,
        slippage_down: fields.slippage_down,
        slippage_up_dec: MathUtil.u64ToRatio(fields.slippage_up),
        slippage_down_dec: MathUtil.u64ToRatio(fields.slippage_down),
        // swap and reward fee percentage
        fee_val: fields.fee_val,
        // withdraw fee percentage
        withdraw_fee_val: fields.withdraw_fee_val,
        // decimals of vault asset TokenA and TokenB
        decimals_a: fields.decimals_a,
        decimals_b: fields.decimals_b,
        coin_type_a: suiStructTag.type_arguments[0],
        coin_type_b: suiStructTag.type_arguments[1],
        lp_token_type: suiStructTag.type_arguments[2],
        config_type: suiStructTag.type_arguments[3],
        is_lock: is_lock,
        is_deposit_enabled: is_deposit_enabled,
        pool_sqrt_price: sqrt_price,

        // protocol_fee_rate: fields.protocol_fee_rate,
        // // is_pause: fields.is_pause,
        // harvest_assets: {
        //   harvest_assets_handle: fields.harvest_assets.fields.id.id,
        //   size: Number(fields.harvest_assets.fields.size),
        // },
        total_supply: fields.treasury_cap.fields.total_supply.fields.value,
        // max_quota: fields.max_quota,
        // // status: fields.status === 1 ? VaultStatus.STATUS_RUNNING : VaultStatus.STATUS_REBALANCING,
        // quota_based_type: fields.quota_based_type.fields.name,
        // position: {},
      }
    }
    return handleMessageError(VaultsErrorCode.BuildError, 'buildVault error')
  }

  public async setSource(coinType: string, isPrimary: boolean, tx: Transaction) {
    const typeArguments = [coinType, PRICE_ADAPTER_PACKAGE_ID + '::price_source::MmtOraclePriceSource'];

    const args = [
      tx.object(ORACLE_ADMIN_CAP_ID),
      tx.object(MMT_ORACLE_ID),
    ];

    tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::oracle::${isPrimary ? 'set_primary_source' : 'set_secondary_source'}`,
      typeArguments,
      arguments: args,
    });

    return tx;
  }

  public async setCoinDecimals(coinType: string, decimals: number, tx: Transaction) {
    const typeArguments = [coinType];

    const args = [
      tx.object(REGISTRY_ADMIN_CAP_ID),
      tx.object(COIN_DECIMALS_REGISTRY),
      tx.pure.u8(decimals),
    ];

    tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::registry::set_coin_decimals`,
      typeArguments,
      arguments: args,
    });

    return tx;
  }

  public async setDepositLimit(vaultId: string, vaultCap: string, depositLimit: bigint, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::vault::set_deposit_limit`,
      typeArguments: [
        vault.coin_type_a,
        vault.coin_type_b,
        vault.lp_token_type,
        vault.config_type
      ],
      arguments: [
        tx.object(vaultId),
        tx.object(vaultCap),
        tx.pure.u64(depositLimit)
      ],
    });

    return tx;
  }

  public async setPricePairId(coinType: string, tx: Transaction) {
    tx.moveCall({
      target: `${PRICE_ADAPTER_PACKAGE_ID}::registry::set_price_pair_id`,
      typeArguments: [coinType],
      arguments: [
        tx.object(PRICE_ADAPTER_ADMIN_CAP),
        tx.object(REGISTRY_ID),
        tx.object(PYTH_ORACLE_MAP[coinType].priceInfoObjectId)
      ],
    });

    return tx;
  }

  public async setSlippage(riskAdminCapId: string, vaultId: string, slippageUp: string, slippageDown: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_slippage`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.pure.u128(MathUtil.toX64Decimal(Decimal(slippageUp).add(1)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(Decimal(1).sub(Decimal(slippageDown))).toFixed(0, Decimal.ROUND_UP)),
      ],
    });

    return tx;
  }

  public async setFreeThresholdA(riskAdminCapId: string, vaultId: string, vaultCap: string, freeThresholdA: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_free_threshold_a`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.object(vaultCap),
        tx.pure.u64(freeThresholdA),
      ],
    });

    return tx;
  }

  public async setFreeThresholdB(riskAdminCapId: string, vaultId: string, vaultCap: string, freeThresholdB: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_free_threshold_b`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.object(vaultCap),
        tx.pure.u64(freeThresholdB),
      ],
    });

    return tx;
  }

  public async setLockThresholdA(riskAdminCapId: string, vaultId: string, vaultCap: string, lockThresholdB: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_lock_threshold_a`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.object(vaultCap),
        tx.pure.u64(lockThresholdB),
      ],
    });

    return tx;
  }

  public async setLockThresholdB(riskAdminCapId: string, vaultId: string, vaultCap: string, lockThresholdB: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_lock_threshold_b`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.object(vaultCap),
        tx.pure.u64(lockThresholdB),
      ],
    });

    return tx;
  }

  public async pauseVault(pauseAdminCapId: string, vaultId: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::pause_vault`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(pauseAdminCapId),
        tx.object(vaultId),
      ],
    });

    return tx;
  }

  public async setPositionPriceScalingForVault(
    vaultConfigAdminCapId: string,
    vaultId: string,
    lowerPriceFactor: string,
    upperPriceFactor: string,
    tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_position_price_scaling_for_vault`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(vaultConfigAdminCapId),
        tx.object(vaultId),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(lowerPriceFactor)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(upperPriceFactor)).toFixed(0, Decimal.ROUND_UP)),
      ],
    });

    return tx;
  }

  public async setTriggerScalling(
    vaultConfigAdminCapId: string,
    vaultId: string,
    lowerTriggerPriceScalling: string,
    upperTriggerPriceScalling: string,
    tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_trigger_scalling`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(vaultConfigAdminCapId),
        tx.object(vaultId),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(lowerTriggerPriceScalling)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(upperTriggerPriceScalling)).toFixed(0, Decimal.ROUND_UP)),
      ],
    });

    return tx;
  }

  public async setUpgeerTriggerPriceFactorDrift(
    vaultConfigAdminCapId: string,
    vaultId: string,
    upperTriggerPriceScalling: string,
    tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_upgeer_trigger_price_factor_drift`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(vaultConfigAdminCapId),
        tx.object(vaultId),
        tx.pure.u128(MathUtil.toX64Decimal(
          new Decimal(upperTriggerPriceScalling)).toFixed(0, Decimal.ROUND_UP)),
      ],
    });

    return tx;
  }

  public async addForceRebalanceDf(riskAdminCapId: string, vaultId: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::add_force_rebalance_df`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
      ],
    });

    return tx;
  }

  public async setDepositEnable(riskAdminCapId: string, vaultId: string, enable: boolean, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_deposit_enable`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.pure.bool(enable),
      ],
    });

    return tx;
  }

  public async unpauseVault(unpauseAdminCapId: string, vaultId: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::unpause_vault`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(unpauseAdminCapId),
        tx.object(vaultId),
      ],
    });

    return tx;
  }

  public async setFee(feeAdminCapId: string, vaultId: string, feeVal: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    if (!vault) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `Vault object not found: ${vaultId}`);
    }

    const fee = Decimal(feeVal).mul(Decimal(Admin.FEE_FACTOR)).toFixed(0, Decimal.ROUND_UP);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_fee`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(feeAdminCapId),
        tx.object(vaultId),
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.u64(fee),
      ],
    });

    return tx;
  }

  public async setWithdrawFee(feeAdminCapId: string, vaultId: string, withdrawFeeVal: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    if (!vault) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `Vault object not found: ${vaultId}`);
    }

    const withdrawFee = Decimal(withdrawFeeVal).mul(Decimal(Admin.FEE_FACTOR)).toFixed(0, Decimal.ROUND_UP);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_withdraw_fee`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(feeAdminCapId),
        tx.object(vaultId),
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.u64(withdrawFee),
      ],
    });

    return tx;
  }

  public async setRebalancePriceSource(rebalanceAdminCap: string, vaultId: string, price_source: number, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_rebalance_price_source`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(rebalanceAdminCap),
        tx.object(vaultId),
        tx.pure.u8(price_source)
      ],
    });

    return tx;
  }

  public async setUcTargetAdapter(rebalanceAdminCapId: string, vaultId: string, vaultCap: string, targetAdapter: string, tx: Transaction) {
    const vault = await this.getVault(vaultId);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_uc_target_adapter`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(rebalanceAdminCapId),
        tx.object(vaultId),
        tx.object(vaultCap),
        tx.pure.string(targetAdapter),
      ],
    });

    return tx;
  }

  public async setTargetAdapter(rebalanceAdminCapId: string, vaultId: string, targetAdapter: string, tx: Transaction) {
    const vault = await this.getVault(vaultId);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_target_adapter`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(rebalanceAdminCapId),
        tx.object(vaultId),
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.string(targetAdapter),
      ],
    });

    return tx;
  }

  public async setUcIsTargetReverse(rebalanceAdminCapId: string, vaultId: string, isTargetReverse: boolean, tx: Transaction) {
    const vault = await this.getVault(vaultId);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_uc_is_target_reverse`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(rebalanceAdminCapId),
        tx.object(vaultId),
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.bool(isTargetReverse),
      ],
    });

    return tx;
  }

  public async issueVaultCap(treasuryAdminCapId: string, vaultId: string, tx: Transaction) {
    const vault = await this.getVault(vaultId);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::issue_vault_cap`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(treasuryAdminCapId),
        tx.object(vaultId),
      ],
    });

    return tx;
  }

  public async setActiveVaultCap(treasuryAdminCapId: string, vaultId: string, vaultCapId: string, tx: Transaction) {
    const vault = await this.getVault(vaultId);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_active_vault_cap`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type],
      arguments: [
        tx.object(treasuryAdminCapId),
        tx.object(vaultId),
        tx.pure.address(vaultCapId),
      ],
    });

    return tx;
  }

  public async setVaultParameters(
    vaultConfigAdminCapId: string,
    riskAdminCapId: string,
    vaultId: string,
    lowerPriceFactor: string,
    upperPriceFactor: string,
    slippageUp: string,
    slippageDown: string,
    freeThresholdA: string,
    freeThresholdB: string,
    lockThresholdA: string,
    lockThresholdB: string,
    depositLimit: string,
    tx: Transaction) {
    const vault = await this.getVault(vaultId);

    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin::set_vault_parameters`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type],
      arguments: [
        tx.object(vaultConfigAdminCapId),
        tx.object(riskAdminCapId),
        tx.object(vaultId),
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(lowerPriceFactor)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(new Decimal(upperPriceFactor)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(Decimal(slippageUp).add(1)).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u128(MathUtil.toX64Decimal(Decimal(1).sub(Decimal(slippageDown))).toFixed(0, Decimal.ROUND_UP)),
        tx.pure.u64(freeThresholdA),
        tx.pure.u64(freeThresholdB),
        tx.pure.u64(lockThresholdA),
        tx.pure.u64(lockThresholdB),
        tx.pure.u64(depositLimit),
      ],
    });

    return tx;
  }

  public async issueVaultConfigCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_vault_config_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issueRiskAdminCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_risk_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issueFeeAdminCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_fee_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issueRebalanceAdminCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_rebalance_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issueTreasuryAdminCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_treasury_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issuePauseAdminCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_pause_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issueUnpauseAdminCap(userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_unpause_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokeVaultConfigCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_vault_config_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokeRiskAdminCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_risk_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokeFeeAdminCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_fee_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokeRebalanceAdminCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_rebalance_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokeTreasuryAdminCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_treasury_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokePauseAdminCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_pause_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async revokeUnpauseAdminCap(capId: string, userAddress: string, tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::revoke_unpause_admin_cap`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.object(capId),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap], userAddress)

    return tx;
  }

  public async issueAllCapabilities(userAddress: string, tx: Transaction) {
    const [cap0, cap1, cap2, cap3, cap4, cap5, cap6] = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_all_capabilities`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects([cap0, cap1, cap2, cap3, cap4, cap5, cap6], userAddress)

    return tx;
  }

  public async issueRebalanceCap(vaultCapId: string, userAddress: string, tx: Transaction) {
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::vault::issue_rebalance_cap`,
      typeArguments: [],
      arguments: [
        tx.object(vaultCapId),
        tx.pure.address(userAddress),
        tx.object(VAULT_VERSION_ID),
      ],
    });
    return tx;
  }

  public async issueSpecificCapabilities(
    userAddress: string,
    issueVaultConfig: boolean,
    issueRisk: boolean,
    issueFee: boolean,
    issueRebalance: boolean,
    issueTreasury: boolean,
    issuePause: boolean,
    issueUnpause: boolean,
    tx: Transaction) {
    const cap = tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::admin_control::issue_specific_capabilities`,
      typeArguments: [],
      arguments: [
        tx.object(VAULT_ADMIN_CAP),
        tx.pure.bool(issueVaultConfig),
        tx.pure.bool(issueRisk),
        tx.pure.bool(issueFee),
        tx.pure.bool(issueRebalance),
        tx.pure.bool(issueTreasury),
        tx.pure.bool(issuePause),
        tx.pure.bool(issueUnpause),
        tx.pure.address(userAddress),
      ],
    });

    tx.transferObjects(cap, userAddress)

    return tx;
  }

  public async addSwapRoute(
    vaultId: string,
    vaultCap: string,
    poolId: string,
    returnsX: boolean,
    xToY: boolean,
    typeArg: string,
    tx: Transaction,
    poolId1?: string,
    xToY1?: boolean,
  ) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    if (!vault) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `Vault object not found:${vaultId}`);
    }
    const singleValue = tx.moveCall({
      target: `${MMT_ROUTER_PACKAGE_ID}::bag_value::new`,
      typeArguments: [],
      arguments: [
        tx.pure.address(poolId),
        tx.pure.bool(xToY)
      ],
    });
    const vectorValue = tx.moveCall({
      target: `0x1::vector::empty`,
      typeArguments: [`${MMT_ROUTER_PACKAGE_ID}::bag_value::Value`],
    });
    tx.moveCall({
      target: `0x1::vector::push_back`,
      typeArguments: [`${MMT_ROUTER_PACKAGE_ID}::bag_value::Value`],
      arguments: [vectorValue, singleValue],
    });
    if (poolId1 !== undefined && xToY1 !== undefined) {
      const sedValue = tx.moveCall({
        target: `${MMT_ROUTER_PACKAGE_ID}::bag_value::new`,
        typeArguments: [],
        arguments: [
          tx.pure.address(poolId1),
          tx.pure.bool(xToY1)
        ],
      });
      tx.moveCall({
        target: `0x1::vector::push_back`,
        typeArguments: [`${MMT_ROUTER_PACKAGE_ID}::bag_value::Value`],
        arguments: [vectorValue, sedValue],
      })
    }
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::route::add`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type, typeArg],
      arguments: [
        tx.object(vaultCap),
        tx.object(vaultId),
        vectorValue,
        tx.pure.bool(returnsX),
        tx.object(VAULT_VERSION_ID),
      ],
    });

    return tx;
  }

  public async removeSwapRoute(vaultId: string, vaultCap: string, typeArg: string, tx: Transaction) {
    const vaultObj = await this._config.suiClient.getObject({
      id: vaultId,
      options: {showType: true, showContent: true, showDisplay: true, showOwner: true},
    });
    const vault = await this.buildVaultWithoutLiquidityAndPosition(vaultObj);
    if (!vault) {
      return handleMessageError(VaultsErrorCode.ObjectNotFound, `Vault object not found:${vaultId}`);
    }
    tx.moveCall({
      target: `${VAULT_PACKAGE_ID}::route::remove`,
      typeArguments: [vault.coin_type_a, vault.coin_type_b, vault.lp_token_type, vault.config_type, typeArg],
      arguments: [
        tx.object(vaultCap),
        tx.object(vaultId),
        tx.object(VAULT_VERSION_ID),
      ],
    });

    return tx;
  }

  public async updateStalenessThreshold(stalenessThreshold: number, tx: Transaction) {
    tx.moveCall({
      target: `${MMT_ORACLE_PACKAGE_ID}::oracle::update_staleness_threshold`,
      typeArguments: [],
      arguments: [
        tx.object(ORACLE_ADMIN_CAP_ID),
        tx.object(MMT_ORACLE_ID),
        tx.pure.u64(stalenessThreshold)
      ],
    });

    return tx;
  }

}

interface UncorrelatedVaultParam {
  pool_id: string;
  treasury_cap_id: string;
  upper_price_scalling: string,
  lower_price_scalling: string,
  slippage_up: string,
  slippage_down: string,
  free_threshold_a: string,
  free_threshold_b: string,
  lock_threshold_a: string,
  lock_threshold_b: string,
  fee_val: string,
  withdraw_fee_val: string,
  decimals_a: number,
  decimals_b: number,
  deposit_limit: string,
  target_adapter: string,
  is_target_reverse: boolean,
  coin_type_a: string,
  coin_type_b: string,
  coin_type_token: string,
}

interface StableVaultParam {
  pool_id: string;
  treasury_cap_id: string;
  upper_price_scalling: string,
  lower_price_scalling: string,
  slippage_up: string,
  slippage_down: string,
  free_threshold_a: string,
  free_threshold_b: string,
  fee_val: string,
  withdraw_fee_val: string,
  decimals_a: number,
  decimals_b: number,
  deposit_limit: string,
  coin_type_a: string,
  coin_type_b: string,
  coin_type_token: string,
}

interface DriftVaultParam {
  pool_id: string;
  treasury_cap_id: string;
  upper_price_scalling: string,
  lower_price_scalling: string,
  slippage_up: string,
  slippage_down: string,
  free_threshold_a: string,
  free_threshold_b: string,
  lock_threshold_a: string,
  lock_threshold_b: string,
  fee_val: string,
  withdraw_fee_val: string,
  decimals_a: number,
  decimals_b: number,
  deposit_limit: string,
  target_adapter: string,
  coin_type_a: string,
  coin_type_b: string,
  coin_type_token: string,
}

interface SeedParam {
  vault_id: string;
  vault_cap: string;
  asset_a: string;
  asset_b: string;
}
